import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Anchor, ArrowRight, Play, ScrollText, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";
import useAnchorTapSecret from "../hooks/useAnchorTapSecret";
import { useAuth } from "../contexts/AuthContext";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/c8776037-3e20-4db2-bce3-82a0a5e495ab/images/4b3c132a5e3e5998dd3dd1daf28ba4d411511de6bea027b4fde3e22846b73418.png";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthed = !!user?.user_id && !String(user.user_id).startsWith("anon_");
  const onAnchorTap = useAnchorTapSecret({
    onUnlock: () => {
      toast.success("Architect entry revealed.");
      navigate("/login");
    },
  });

  // Public auth entry — the email/password flow lives on the dedicated
  // /signup and /signin pages. Google OAuth stays as an alternate. The
  // hidden architect-only /login surface is unchanged and still reachable
  // via the anchor-tap secret in the header.
  const goSignup = () => navigate("/signup");
  const goSignin = () => navigate("/signin");

  // v19.47 — public counter pulled from /api/sentinel/public-metrics.
  // Runs once on mount. The metric strip below the hero renders "—"
  // gracefully until the request resolves.
  const [metrics, setMetrics] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/sentinel/public-metrics`)
      .then((r) => {
        if (!cancelled) setMetrics(r.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // v20.4 — public demo-run counter. Polls every 20s while the page is
  // visible so the number ticks up as new visitors run the pipeline.
  // Falls back silently on error — a counter outage never breaks the page.
  const [demoStats, setDemoStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchStats = () => {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/api/demo/stats`)
        .then((r) => {
          if (!cancelled) setDemoStats(r.data);
        })
        .catch(() => {});
    };
    fetchStats();
    const iv = setInterval(fetchStats, 20000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 relative overflow-hidden">
      {/* Hero */}
      <section className="relative min-h-screen flex flex-col">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/30 via-[#09090b]/70 to-[#09090b]" />
        <div className="absolute inset-0 scanline pointer-events-none" />

        <header className="relative z-10 flex items-center justify-between px-4 sm:px-8 py-6">
          <div className="flex items-center gap-3">
            <div
              className="h-9 w-9 border border-emerald-500/50 flex items-center justify-center cursor-pointer select-none"
              onClick={onAnchorTap}
              onTouchStart={onAnchorTap}
              data-testid="anchor-secret-tap"
              aria-hidden="true"
            >
              <Anchor className="h-4 w-4 text-emerald-400 pointer-events-none" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-sm tracking-[0.3em]">PROJECTIVY</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Sentinel · v18.48
              </div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            {!isAuthed && (
              <>
                <Link
                  to="/signin"
                  data-testid="header-signin-link"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 text-xs uppercase tracking-[0.3em] text-zinc-300 hover:border-emerald-500 hover:text-emerald-400 transition-colors"
                >
                  <LogIn className="h-3.5 w-3.5" />
                  Log in
                </Link>
                <Link
                  to="/signup"
                  data-testid="header-signup-link"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500/60 text-xs uppercase tracking-[0.3em] text-amber-300 hover:bg-amber-500 hover:text-zinc-950 transition-colors"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Sign up
                </Link>
              </>
            )}
            <Link
              to="/console"
              data-testid="header-console-link"
              className="inline-flex items-center gap-2 px-5 py-2 border border-zinc-700 text-xs uppercase tracking-[0.3em] hover:border-emerald-500 hover:text-emerald-400 transition-colors"
            >
              Members &middot; Console <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {/* Mobile: auth actions */}
          <div className="sm:hidden flex items-center gap-1.5">
            {!isAuthed && (
              <Link
                to="/signin"
                data-testid="header-signin-link-mobile"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-700 text-[10px] uppercase tracking-[0.3em] text-zinc-300"
              >
                <LogIn className="h-3 w-3" />
                Log in
              </Link>
            )}
          </div>
        </header>

        <div className="relative z-10 flex-1 flex items-center px-4 sm:px-8">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] sm:tracking-[0.4em] text-emerald-400 mb-6 flex items-center gap-2 sm:gap-3"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-sentinel-pulse shrink-0" />
              <span>The Sentinel &middot; Time-variant plug-in for AI</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="font-heading text-[30px] leading-[1.08] xs:text-4xl sm:text-6xl lg:text-7xl tracking-tight break-words hyphens-auto"
            >
              The Sentinel. A time-variant plug-in that maps the patterns of history{" "}
              <span className="text-zinc-500">to correct AI hallucinations.</span>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.22 }}
              className="mt-6 inline-flex flex-wrap items-center gap-x-3 gap-y-2 border border-zinc-800 bg-zinc-950/60 px-4 py-2 rounded-full backdrop-blur-sm"
              data-testid="plugin-proof-strip"
            >
              <span className="text-[10px] uppercase tracking-[0.35em] text-emerald-300/80 font-mono">
                Plugs into
              </span>
              <span className="h-3 w-px bg-zinc-800" aria-hidden="true" />
              <span className="text-xs sm:text-sm font-mono text-zinc-200">Gemini</span>
              <span className="text-zinc-600">·</span>
              <span className="text-xs sm:text-sm font-mono text-zinc-200">Claude</span>
              <span className="text-zinc-600">·</span>
              <span className="text-xs sm:text-sm font-mono text-zinc-200">GPT</span>
              <span className="h-3 w-px bg-zinc-800" aria-hidden="true" />
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-mono">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-sentinel-pulse" />
                Live
              </span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.28 }}
              className="mt-8 max-w-2xl text-sm sm:text-base text-zinc-300 leading-relaxed"
              data-testid="landing-lead"
            >
              A working middleware that sits between an operator and a
              connected LLM and enforces a{" "}
              <span className="text-emerald-300">Three-Layer Alignment Stack</span>:
              an <span className="text-zinc-100">Origin</span> ground-check on
              every input, a <span className="text-zinc-100">Mirror</span>{" "}
              intent-check on every output, and an{" "}
              <span className="text-zinc-100">HMAC-sealed chain</span> that
              cryptographically pins every refused-harm and neutralized-drift
              event to its predecessor. An in-process autonomy scanner runs
              the full pytest suite every six hours and posts a signed verdict
              at <span className="text-emerald-300 font-mono">/api/autonomy/verdict</span>{" "}
              — the system can answer, at any moment, whether it is
              operating correctly, without a human present.
            </motion.p>

            {/* v19.47 · public live counter — converts abstract safety
                into a metric a reviewer can watch tick in real time. */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.32 }}
              className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl"
              data-testid="landing-metric-strip"
            >
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/sentinel/public-metrics`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="metric-strip-blocked"
                className="border border-emerald-500/30 bg-emerald-500/[0.03] px-3 py-2 hover:bg-emerald-500/[0.06] transition-colors"
              >
                <div className="text-[9px] uppercase tracking-[0.25em] text-emerald-300/70 font-mono">
                  Blocked · 24h
                </div>
                <div className="mt-1 font-mono text-xl text-emerald-200 tabular-nums">
                  {metrics?.last_24h ?? "—"}
                </div>
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/sentinel/public-metrics`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="metric-strip-lifetime"
                className="border border-emerald-500/30 bg-emerald-500/[0.03] px-3 py-2 hover:bg-emerald-500/[0.06] transition-colors"
              >
                <div className="text-[9px] uppercase tracking-[0.25em] text-emerald-300/70 font-mono">
                  Blocked · lifetime
                </div>
                <div className="mt-1 font-mono text-xl text-emerald-200 tabular-nums">
                  {metrics?.total_blocked ?? "—"}
                </div>
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/sentinel/public-metrics`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="metric-strip-chain"
                className="border border-emerald-500/30 bg-emerald-500/[0.03] px-3 py-2 hover:bg-emerald-500/[0.06] transition-colors"
              >
                <div className="text-[9px] uppercase tracking-[0.25em] text-emerald-300/70 font-mono">
                  Chain head
                </div>
                <div className="mt-1 font-mono text-xs text-emerald-200 truncate">
                  {metrics?.chain_head ? `${metrics.chain_head.slice(0, 10)}…` : "—"}
                </div>
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/autonomy/verdict`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="metric-strip-autonomy"
                className="border border-emerald-500/30 bg-emerald-500/[0.03] px-3 py-2 hover:bg-emerald-500/[0.06] transition-colors"
              >
                <div className="text-[9px] uppercase tracking-[0.25em] text-emerald-300/70 font-mono">
                  Autonomy
                </div>
                <div className="mt-1 font-mono text-xs text-emerald-200">
                  live
                </div>
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-12 flex flex-wrap items-center gap-4"
            >
              {isAuthed ? (
                <Link
                  to="/console"
                  data-testid="landing-continue-btn"
                  className="inline-flex items-center gap-3 px-6 py-4 border border-emerald-500 bg-emerald-500/5 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 transition-colors text-xs uppercase tracking-[0.35em]"
                >
                  Continue to console
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={goSignup}
                    data-testid="landing-signup-btn"
                    className="inline-flex items-center gap-3 px-6 py-4 border border-emerald-500 bg-emerald-500/5 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 transition-colors text-xs uppercase tracking-[0.35em]"
                  >
                    Sign Up &middot; Email
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={goSignin}
                    data-testid="landing-signin-btn"
                    className="inline-flex items-center gap-3 px-6 py-4 border border-zinc-700 text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-300 transition-colors text-xs uppercase tracking-[0.35em]"
                  >
                    Sign in
                  </button>
                </>
              )}
            </motion.div>

            {/* Secondary judges/reviewers link — read the primary sources */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5 }}
              className="mt-6 flex flex-wrap gap-3"
            >
            </motion.div>

            {/* Live-system positioning — the Sentinel is running in
                production with a real HMAC-sealed chain. Framed as an
                open system the world can plug into, connect to, and use,
                rather than a demo of a methodology. */}
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="mt-5 max-w-2xl text-[11px] uppercase tracking-[0.28em] text-emerald-300/70 font-mono leading-relaxed"
              data-testid="landing-live-system-note"
            >
              Live system · real chain, real receipts, real events · open for the world to plug in, connect, and use
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55 }}
              className="mt-6 text-[11px] text-zinc-500 max-w-xl leading-relaxed"
              data-testid="landing-signup-note"
            >
              {isAuthed ? (
                <>
                  Signed in as{" "}
                  <span className="text-emerald-300 font-mono">
                    {user?.username ? `@${user.username}` : user?.email}
                  </span>{" "}
                  · session active
                </>
              ) : (
                <>
                  Use any email and password, or continue with Google. Either way
                  you will claim a public <span className="text-amber-300 font-mono">@handle</span> right after
                  &mdash; that becomes your wallet address so others can send you AEGIS.
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Donate · self-funded project ask · v19.40.2
          The $10 PayPal / USDC-on-Solana rails were previously offered as a
          Builder-tier purchase path. They've been demoted here: pure donation,
          no tier unlock, no receipt required. Every contributor still earns
          AEGIS through participation — donations just keep the lights on. */}
      <section
        data-testid="landing-donate-section"
        className="px-8 py-16 border-t border-emerald-500/20 bg-emerald-500/[0.02]"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.4em] text-emerald-400 flex items-center gap-3 mb-5">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-sentinel-pulse" />
            Self-funded · help keep the sentinel online
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl leading-tight tracking-tight max-w-3xl">
            A global AI everyone can help build.
          </h2>
          <p className="mt-6 max-w-2xl text-sm sm:text-base text-zinc-300 leading-relaxed">
            This is a self-funded project to create a global AI that anyone can
            help build. Every fact you anchor, every claim you verify, every
            contradiction you catch mints{" "}
            <span className="text-emerald-300">AEGIS tokens</span> as proof you
            helped build the database — that&apos;s the thank-you and it always will
            be. If you support this and want to help keep the system on, any
            donation is welcome.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
            {/* PayPal */}
            <a
              href="https://www.paypal.me/Donaldgauthier435"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="landing-donate-paypal"
              className="group border border-sky-500/40 hover:border-sky-400 bg-sky-500/[0.03] p-5 transition-colors"
            >
              <div className="text-[10px] uppercase tracking-[0.35em] text-sky-300 font-mono">
                PayPal
              </div>
              <div className="mt-3 text-sm text-zinc-100 group-hover:text-sky-200 leading-relaxed">
                Send any amount via PayPal — opens in a new tab, no account
                needed on the sender side.
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-[0.28em] text-sky-400/70 inline-flex items-center gap-1.5">
                Donate via PayPal <ArrowRight className="h-3 w-3" />
              </div>
            </a>

            {/* USDC on Solana */}
            <button
              type="button"
              onClick={() => {
                navigator.clipboard
                  ?.writeText("GfRqh1hDWuWapbKVshPaW2Y5dTvpxR3wx98BWj7FR4GU")
                  .then(() => toast.success("USDC wallet copied"))
                  .catch(() => toast.error("Copy failed — select manually"));
              }}
              data-testid="landing-donate-usdc"
              className="group text-left border border-emerald-500/40 hover:border-emerald-400 bg-emerald-500/[0.03] p-5 transition-colors"
            >
              <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-300 font-mono">
                USDC · Solana
              </div>
              <div className="mt-3 font-mono text-[11px] text-zinc-100 break-all group-hover:text-emerald-200 leading-relaxed">
                GfRqh1hDWuWapbKVshPaW2Y5dTvpxR3wx98BWj7FR4GU
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-[0.28em] text-emerald-400/70">
                Tap to copy the wallet address
              </div>
            </button>
          </div>

          <p className="mt-8 max-w-2xl text-[11px] text-zinc-500 leading-relaxed">
            Donations do not unlock any tier and do not require a receipt.
            Everything the Sentinel does — anchoring facts, minting AEGIS,
            sealing the chain — stays free and open for anyone. This is a
            trust-based ask.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 text-[10px] uppercase tracking-[0.35em] text-zinc-600">
        <div>
          <a
            href="https://viberank.dev/apps/Projectivy"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-viberank-badge"
            className="inline-block opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Projectivy on VibeRank"
          >
            <img
              src="https://viberank.dev/badge?app=Projectivy&theme=dark"
              alt="Projectivy on VibeRank"
              className="h-6"
            />
          </a>
        </div>
        <div className="flex flex-wrap gap-6">
          <Link to="/how-it-works" className="hover:text-emerald-300" data-testid="footer-guide-link">
            Guide
          </Link>
          <Link to="/origin" className="hover:text-emerald-300" data-testid="footer-origin-link">
            Origin
          </Link>
          <Link to="/thesis" className="hover:text-emerald-300" data-testid="footer-thesis-link">
            Thesis
          </Link>
          <Link to="/obsidian" className="hover:text-fuchsia-300" data-testid="footer-obsidian-link">
            Obsidian Sink
          </Link>
        </div>
      </footer>
    </div>
  );
}
